import { addDaysToDate, formatDate, getDateDifference, getStartOfDay, parseDate } from '@travelata-libs/utils';
import type { DateRange } from '@/shared/model';

export const LOW_PRICE_CALENDAR_DAYS = 31;

const CALENDAR_SPAN_IN_DAYS = LOW_PRICE_CALENDAR_DAYS - 1;
const ISO_DATE_FORMAT = 'yyyy-MM-dd';

export function getCalendarDateRange(
  dateRange: DateRange | null,
  today: Date = new Date(),
): { from: string; to: string } | null {
  if (!dateRange) {
    return null;
  }

  const selectedFrom = parseDate(dateRange.from);
  const selectedTo = parseDate(dateRange.to);

  if (!selectedFrom || !selectedTo) {
    return null;
  }

  const selectionStart = getStartOfDay(selectedFrom);
  const selectionEnd = getStartOfDay(selectedTo);
  const tomorrow = addDaysToDate(getStartOfDay(today), 1);

  const selectionSpanInDays = getDateDifference(selectionEnd, selectionStart, 'days', false);

  if (selectionSpanInDays > 0) {
    const calendarStart = getDateDifference(selectionStart, tomorrow, 'days', false) < 0 ? tomorrow : selectionStart;

    return {
      from: formatDate(calendarStart, ISO_DATE_FORMAT),
      to: formatDate(addDaysToDate(calendarStart, CALENDAR_SPAN_IN_DAYS), ISO_DATE_FORMAT),
    };
  }

  const daysToAdd = CALENDAR_SPAN_IN_DAYS - selectionSpanInDays;
  const daysBeforeSelection = Math.floor(daysToAdd / 2);
  const daysAfterSelection = daysBeforeSelection + (daysToAdd % 2);

  const centredStart = addDaysToDate(selectionStart, -daysBeforeSelection);
  const startsBeforeTomorrow = getDateDifference(centredStart, tomorrow, 'days', false) < 0;

  const calendarStart = startsBeforeTomorrow ? tomorrow : centredStart;
  const calendarEnd = startsBeforeTomorrow
    ? addDaysToDate(tomorrow, CALENDAR_SPAN_IN_DAYS)
    : addDaysToDate(selectionEnd, daysAfterSelection);

  return {
    from: formatDate(calendarStart, ISO_DATE_FORMAT),
    to: formatDate(calendarEnd, ISO_DATE_FORMAT),
  };
}

export function getNextCalendarDateRange(loadedUntil: string): { from: string; to: string } | null {
  const lastLoaded = parseDate(loadedUntil);

  if (!lastLoaded) {
    return null;
  }

  const nextStart = addDaysToDate(getStartOfDay(lastLoaded), 1);

  return {
    from: formatDate(nextStart, ISO_DATE_FORMAT),
    to: formatDate(addDaysToDate(nextStart, CALENDAR_SPAN_IN_DAYS), ISO_DATE_FORMAT),
  };
}

export function getPreviousCalendarDateRange(
  loadedFrom: string,
  today: Date = new Date(),
): { from: string; to: string } | null {
  const firstLoaded = parseDate(loadedFrom);

  if (!firstLoaded) {
    return null;
  }

  const previousEnd = addDaysToDate(getStartOfDay(firstLoaded), -1);
  const tomorrow = addDaysToDate(getStartOfDay(today), 1);

  if (getDateDifference(previousEnd, tomorrow, 'days', false) < 0) {
    return null;
  }

  const previousStart = addDaysToDate(previousEnd, -CALENDAR_SPAN_IN_DAYS);
  const startsBeforeTomorrow = getDateDifference(previousStart, tomorrow, 'days', false) < 0;

  return {
    from: formatDate(startsBeforeTomorrow ? tomorrow : previousStart, ISO_DATE_FORMAT),
    to: formatDate(previousEnd, ISO_DATE_FORMAT),
  };
}

function isSelectionInside(dateRange: DateRange, calendarRange: { from: string; to: string }): boolean {
  const selectedFrom = parseDate(dateRange.from);
  const selectedTo = parseDate(dateRange.to);

  if (!selectedFrom || !selectedTo) {
    return false;
  }

  const from = formatDate(selectedFrom, ISO_DATE_FORMAT);
  const to = formatDate(selectedTo, ISO_DATE_FORMAT);

  return from >= calendarRange.from && to <= calendarRange.to;
}

export function resolveCalendarDateRange(
  dateRange: DateRange | null,
  calendarRange: { from: string; to: string } | null,
  today: Date = new Date(),
): { from: string; to: string } | null {
  if (dateRange && calendarRange && dateRange.from === dateRange.to && isSelectionInside(dateRange, calendarRange)) {
    return calendarRange;
  }

  return getCalendarDateRange(dateRange, today);
}
